import type { EmployeeStatus } from '@hygge/shared';
import { EMPLOYEE_STATUSES } from '@hygge/shared';
import type { FilterConfig, FilterOption } from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * Filter state for employee listing/filtering.
 * Uses proper union types for type safety in API params.
 */
export interface EmployeeFilters {
  status: EmployeeStatus | 'all' | 'current_staff';
  departmentId: string;
  positionId: string;
  positionIds?: string[];
  managerId: string;
  seniorityLevelId: string;
  employmentTypeId: string;
  googleLinked: string;
  compensationReview: 'all' | 'date_set' | 'not_scheduled';
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default values for employee filters.
 * "current_staff" shows active + probation employees by default.
 */
export const DEFAULT_EMPLOYEE_FILTERS: EmployeeFilters = {
  status: 'current_staff',
  departmentId: 'all',
  positionId: 'all',
  managerId: 'all',
  seniorityLevelId: 'all',
  employmentTypeId: 'all',
  googleLinked: 'all',
  compensationReview: 'all',
};

/**
 * Status options including the special "current_staff" option.
 */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'current_staff', label: 'Current Staff' },
  ...EMPLOYEE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
];

// ============================================================================
// Configuration
// ============================================================================

/**
 * Complete filter configuration for employee listing.
 * Used on the People page and anywhere employees need to be filtered.
 */
export const employeeFilterConfig: FilterConfig<EmployeeFilters> = {
  id: 'employee-filters',
  title: 'Filters',
  defaultValues: DEFAULT_EMPLOYEE_FILTERS,

  fields: [
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'All statuses',
      defaultValue: 'current_staff',
      options: STATUS_OPTIONS,
    },
    {
      key: 'departmentId',
      type: 'select',
      label: 'Department',
      placeholder: 'All departments',
      defaultValue: 'all',
      options: 'departments',
    },
    {
      key: 'positionId',
      type: 'select',
      label: 'Position',
      placeholder: 'All positions',
      defaultValue: 'all',
      options: 'positions',
    },
    {
      key: 'managerId',
      type: 'search',
      label: 'Manager',
      placeholder: 'All managers',
      defaultValue: 'all',
      options: 'managers',
    },
    {
      key: 'employmentTypeId',
      type: 'select',
      label: 'Employment Type',
      placeholder: 'All types',
      defaultValue: 'all',
      options: 'employmentTypes',
    },
    {
      key: 'seniorityLevelId',
      type: 'select',
      label: 'Seniority Level',
      placeholder: 'All levels',
      defaultValue: 'all',
      options: 'seniorityLevels',
    },
    {
      key: 'googleLinked',
      type: 'select',
      label: 'Google Account',
      placeholder: 'All',
      defaultValue: 'all',
      options: [
        { value: 'true', label: 'Connected' },
        { value: 'false', label: 'Not connected' },
      ],
    },
    {
      key: 'compensationReview',
      type: 'select',
      label: 'Compensations',
      placeholder: 'All types',
      defaultValue: 'all',
      options: [
        { value: 'date_set', label: 'Date set' },
        { value: 'not_scheduled', label: 'Not scheduled' },
      ],
    },
  ],

  /**
   * Transform filter state to API query parameters.
   * Handles special cases like "current_staff" expanding to multiple statuses.
   */
  toQueryParams: (filters) => {
    const params: Record<string, unknown> = {};

    // Handle status filter
    if (filters.status === 'current_staff') {
      params.statuses = ['active', 'probation', 'onboarding'];
    } else if (filters.status !== 'all') {
      params.status = filters.status;
    }

    // Handle department
    if (filters.departmentId !== 'all') {
      params.departmentId = filters.departmentId;
    }

    // Handle position(s)
    if (filters.positionIds && filters.positionIds.length > 0) {
      params.positionIds = filters.positionIds;
    } else if (filters.positionId !== 'all') {
      params.positionId = filters.positionId;
    }

    // Handle manager
    if (filters.managerId !== 'all') {
      params.managerId = filters.managerId;
    }

    // Handle seniority level
    if (filters.seniorityLevelId !== 'all') {
      params.seniorityLevelId = filters.seniorityLevelId;
    }

    // Handle employment type
    if (filters.employmentTypeId !== 'all') {
      params.employmentTypeId = filters.employmentTypeId;
    }

    // Handle Google account linked
    if (filters.googleLinked !== 'all') {
      params.googleLinked = filters.googleLinked;
    }

    // Compensation-review status
    if (filters.compensationReview !== 'all') {
      params.compensationReview = filters.compensationReview;
    }

    return params;
  },

  /**
   * Handle filter changes with special logic.
   */
  onChange: (filters, changedKey) => {
    // When changing positionId, clear positionIds (category filter)
    if (
      changedKey === 'positionId' &&
      filters.positionId !== 'all' &&
      filters.positionIds &&
      filters.positionIds.length > 0
    ) {
      filters.positionIds = undefined;
    }

    // When changing status away from current_staff, clear positionIds
    if (
      changedKey === 'status' &&
      filters.status !== 'current_staff' &&
      filters.positionIds &&
      filters.positionIds.length > 0
    ) {
      filters.positionIds = undefined;
      filters.positionId = 'all';
    }
  },
};
